import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * Página pública /sub-processadores (LGPD Art. 39 + GDPR Art. 28).
 *
 * Carrega lista de /api/legal/sub-processors (gerenciada pelo admin
 * em /admin/sub-processors). Fallback: mensagem de "sem dados" se
 * API indisponível — nunca quebra o layout.
 */

interface SubProcessor {
  id: string
  name: string
  purpose: string
  dataTypes: string[]
  location: string
  legalBasis: string
  link: string | null
}

export default function SubProcessors() {
  const [items, setItems] = useState<SubProcessor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch('/api/legal/sub-processors')
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setItems(d.subProcessors))
      .catch(err => setError(err.message || 'Falha ao carregar'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/privacidade" className="text-sm text-indigo-600 hover:text-indigo-800">Política de Privacidade</Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Sub-processadores</h1>
        <p className="text-gray-600 mb-6">
          Esta lista identifica os sub-operadores que a PAKT TECNOLOGIA LTDA contrata pra processar
          dados pessoais em nome do controlador (cliente B2B), conforme LGPD Art. 39 e GDPR Art. 28.
          Atualizamos esta página quando trocamos de fornecedor.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-sm text-blue-900">
          <strong>Notificação prévia:</strong> antes de adicionar um novo sub-processador,
          notificamos clientes Enterprise por e-mail com pelo menos 15 dias de antecedência.
          Pra ativar notificação, escreva pra <a href="mailto:privacidade@pakt.io" className="underline">privacidade@pakt.io</a>.
        </div>

        {loading ? (
          <p className="text-gray-500">Carregando...</p>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
            Não foi possível carregar a lista no momento.
          </div>
        ) : items.length === 0 ? (
          <p className="text-gray-500">Nenhum sub-processador registrado.</p>
        ) : (
          <div className="space-y-4">
            {items.map(sp => (
              <article key={sp.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">{sp.name}</h2>
                  {sp.link && (
                    <a href={sp.link} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-indigo-600 hover:underline whitespace-nowrap">
                      Privacidade ↗
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Finalidade</div>
                    <div className="text-gray-800">{sp.purpose}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Localização</div>
                    <div className="text-gray-800">{sp.location}</div>
                  </div>
                  {sp.dataTypes.length > 0 && (
                    <div className="md:col-span-2">
                      <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Dados tratados</div>
                      <ul className="list-disc pl-5 text-gray-800 text-sm">
                        {sp.dataTypes.map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Base legal pra transferência</div>
                    <div className="text-gray-800">{sp.legalBasis}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-600">
          <p>
            Para perguntas sobre sub-processadores, escreva pra{' '}
            <a href="mailto:privacidade@pakt.io" className="text-indigo-600 hover:underline">privacidade@pakt.io</a>{' '}
            ou exerça seus direitos em{' '}
            <Link to="/privacidade/exercer-direitos" className="text-indigo-600 hover:underline">formulário online</Link>.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </p>
        </div>
      </main>
    </div>
  )
}
